import { FormEvent, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { extractApiError } from "@/lib/utils";

const LONGITUD_MINIMA = 10;

/**
 * Rotación obligatoria de la contraseña provisional.
 *
 * Se muestra en lugar de la aplicación cuando el servidor marca
 * `debe_cambiar_password`: la contraseña con la que el usuario acaba de
 * entrar la eligió un administrador, así que un tercero la conoce.
 */
export function CambiarPasswordPage() {
  const { cambiarPassword, cerrar, sesion } = useAuth();

  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const coinciden = nueva.length > 0 && nueva === repetida;
  const suficiente = nueva.length >= LONGITUD_MINIMA;
  const listo = actual.length > 0 && coinciden && suficiente && !enviando;

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await cambiarPassword(actual, nueva);
    } catch (err) {
      setError(extractApiError(err, "No se pudo cambiar la contraseña."));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="min-h-screen grid place-items-center"
      style={{ background: "var(--bg-2)" }}
    >
      <div className="w-full max-w-md p-10 stack stack-5">
        <div className="text-center stack stack-3">
          <div
            className="brand-mark mx-auto"
            style={{ width: 44, height: 44, fontSize: 16 }}
          >
            G
          </div>
          <div>
            <h1
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 28,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Cambia tu contraseña
            </h1>
            <p style={{ color: "var(--ink-3)", fontSize: 14, margin: 0 }}>
              La actual es provisional y la conoce quien creó tu cuenta
              {sesion ? ` (${sesion.sub})` : ""}.
            </p>
          </div>
        </div>

        <form onSubmit={manejarEnvio} className="stack stack-4">
          <div className="field">
            <label htmlFor="actual">Contraseña actual</label>
            <input
              id="actual"
              type="password"
              required
              autoFocus
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              className="input"
              autoComplete="current-password"
            />
          </div>

          <div className="field">
            <label htmlFor="nueva">Nueva contraseña</label>
            <input
              id="nueva"
              type="password"
              required
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
              className="input"
              autoComplete="new-password"
            />
            <span className="hint">
              Mínimo {LONGITUD_MINIMA} caracteres. Si usas menos de 16, combina
              mayúsculas, minúsculas, dígitos o símbolos. Una frase larga es
              más segura y más fácil de recordar.
            </span>
          </div>

          <div className="field">
            <label htmlFor="repetida">Repite la nueva contraseña</label>
            <input
              id="repetida"
              type="password"
              required
              value={repetida}
              onChange={(e) => setRepetida(e.target.value)}
              className="input"
              autoComplete="new-password"
            />
            {repetida.length > 0 && !coinciden && (
              <span className="hint" style={{ color: "var(--red)" }}>
                Las contraseñas no coinciden.
              </span>
            )}
          </div>

          {error && (
            <div className="banner banner--red">
              <span className="banner__icon">⚠</span>
              <div>{error}</div>
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary btn--block btn--lg"
            disabled={!listo}
          >
            {enviando ? "Guardando…" : "Guardar y continuar"}
          </button>
        </form>

        <button type="button" className="btn btn--ghost btn--block" onClick={cerrar}>
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
